import { Button } from "@altimateai/lego";
import { Stack, LoadingButton } from "@altimateai/lego";
import { CreateTestCaseRequest } from "../../services/evals";
import { TestCaseForm } from "./TestCaseForm";

interface TestCaseRowEditModeProps {
  editingTestCaseData: CreateTestCaseRequest;
  setEditingTestCaseData: (data: CreateTestCaseRequest) => void;
  currentTenant: string;
  rbacEnabled: boolean;
  teamOptions: Array<{ label: string; value: string }>;
  teamsLoading: boolean;
  criteria?: Array<{ id: string; name: string; description: string }>;
  onSave: () => void;
  onCancel: () => void;
  isSaving: boolean;
}

export const TestCaseRowEditMode: React.FC<TestCaseRowEditModeProps> = ({
  editingTestCaseData,
  setEditingTestCaseData,
  currentTenant,
  rbacEnabled,
  teamOptions,
  teamsLoading,
  criteria,
  onSave,
  onCancel,
  isSaving,
}) => {
  // Convert criteria object to array for the form
  const criteriaToArray = (criteria: Record<string, string> = {}) => {
    return Object.entries(criteria).map(([name, description]) => ({
      name,
      description,
    }));
  };

  // Convert criteria array back to object when updating
  const handleFormChange = (formData: {
    name: string;
    description: string;
    turns: Array<{ user_message: string; expected_ai_response: string }>;
    criteria: Array<{ name: string; description: string }>;
    tags: string[];
    tenant: string;
    team_id: string | null;
    custom_agents: Array<{ name: string; description: string; prompt: string }>;
  }) => {
    // Convert criteria array back to object
    const criteriaObj = formData.criteria.reduce(
      (acc, c) => {
        if (c.name.trim() && c.description.trim()) {
          acc[c.name.trim()] = c.description.trim();
        }
        return acc;
      },
      {} as Record<string, string>
    );

    setEditingTestCaseData({
      ...formData,
      criteria: criteriaObj,
    });
  };

  const formData = {
    name: editingTestCaseData.name,
    description: editingTestCaseData.description || "",
    turns: editingTestCaseData.turns,
    criteria: criteriaToArray(editingTestCaseData.criteria),
    tags: editingTestCaseData.tags || [],
    tenant: editingTestCaseData.tenant || "All",
    team_id: editingTestCaseData.team_id ?? null,
    custom_agents: editingTestCaseData.custom_agents || [],
  };

  return (
    <Stack direction="column" className="al-max-h-[70vh]">
      <div className="al-overflow-y-auto al-overflow-x-hidden al-flex-1 al-pr-2">
        <TestCaseForm
          formData={formData}
          onChange={handleFormChange}
          currentTenant={currentTenant}
          rbacEnabled={rbacEnabled}
          teamOptions={teamOptions}
          teamsLoading={teamsLoading}
          availableCriteria={criteria}
          allowTurnManagement={false}
        />
      </div>

      <Stack
        direction="row"
        gap="sm"
        justify="end"
        className="al-mt-4 al-pt-4 al-border-t al-border-gray-200 al-bg-gray-50 al-sticky al-bottom-0"
      >
        <Button onClick={onCancel} variant="outline" size="sm" disabled={isSaving}>
          Cancel
        </Button>
        <LoadingButton loading={isSaving} onClick={onSave} variant="default" size="sm">
          Save
        </LoadingButton>
      </Stack>
    </Stack>
  );
};
